import React, { useState, useEffect, useRef, useCallback } from 'react';
import { FiX } from 'react-icons/fi';
import GradientBar from './GradientBar';
import { PRESET_COLORS, PRESET_GRADIENTS } from '../../constants/editorConstants';
import {
  isGradientConfig,
  toCssGradient,
  createDefaultGradient,
  hexToRgbObj,
  rgbValuesToHex,
} from '../../utils/gradientUtils';
import { inputStyle, colorInputStyle } from '../../styles/panelStyles';

// HEX 유효성 검증 (#없이 순수 hex 부분)
const isValidHexBody = (hex) => /^[0-9a-fA-F]{6}$/.test(hex);
const isValidHexShort = (hex) => /^[0-9a-fA-F]{3}$/.test(hex);


// 색상 입력 컴포넌트 (HEX/RGB 토글)
const ColorInput = ({ color, onChange, style: wrapperStyle }) => {
  const [mode, setMode] = useState('hex'); // 'hex' | 'rgb'
  const [hexBody, setHexBody] = useState(color.replace('#', '').substring(0, 6));
  const [hexError, setHexError] = useState(false);
  const [rgb, setRgb] = useState(() => hexToRgbObj(color));

  // 외부에서 color가 변경되면 내부 state 동기화
  useEffect(() => {
    if (color && color.startsWith('#')) {
      const body = color.replace('#', '').substring(0, 6);
      setHexBody(body);
      setRgb(hexToRgbObj(color));
      setHexError(false);
    }
  }, [color]);

  const handleHexChange = (e) => {
    const raw = e.target.value.replace(/[^0-9a-fA-F]/g, '').substring(0, 6);
    setHexBody(raw);

    if (isValidHexBody(raw)) {
      setHexError(false);
      const newColor = '#' + raw;
      setRgb(hexToRgbObj(newColor));
      onChange(newColor);
    } else {
      setHexError(raw.length > 0);
    }
  };

  const handleRgbChange = (channel, val) => {
    const num = val === '' ? 0 : Math.max(0, Math.min(255, parseInt(val) || 0));
    const newRgb = { ...rgb, [channel]: num };
    setRgb(newRgb);
    const newColor = rgbValuesToHex(newRgb.r, newRgb.g, newRgb.b);
    setHexBody(newColor.replace('#', ''));
    setHexError(false);
    onChange(newColor);
  };

  const tabBtnStyle = (active) => ({
    padding: '3px 12px',
    border: 'none',
    borderRadius: '4px',
    backgroundColor: active ? '#e0e7ff' : '#f3f4f6',
    color: active ? '#3b82f6' : '#9ca3af',
    fontSize: '11px',
    fontWeight: '600',
    cursor: 'pointer',
    letterSpacing: '0.5px',
  });

  return (
    <div style={wrapperStyle}>
      {/* 토글 버튼 (상단) */}
      <div style={{ display: 'flex', gap: '4px', marginBottom: '8px' }}>
        <button onClick={() => setMode('hex')} style={tabBtnStyle(mode === 'hex')}>HEX</button>
        <button onClick={() => setMode('rgb')} style={tabBtnStyle(mode === 'rgb')}>RGB</button>
      </div>
      {/* 칼라 샘플 + 입력 */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <input
          type="color"
          value={color}
          onChange={(e) => onChange(e.target.value)}
          style={{ width: '40px', height: '36px', border: '1px solid #e5e7eb', borderRadius: '6px', cursor: 'pointer', padding: 0 }}
        />
        {mode === 'hex' ? (
          <div style={{ display: 'flex', alignItems: 'center', position: 'relative' }}>
            <span style={{
              fontSize: '13px', color: '#9ca3af', fontWeight: '600',
              position: 'absolute', left: '10px', pointerEvents: 'none', zIndex: 1,
            }}>#</span>
            <input
              type="text"
              value={hexBody}
              onChange={handleHexChange}
              maxLength={6}
              style={{
                ...inputStyle,
                fontSize: '13px',
                width: '120px',
                height: '36px',
                paddingLeft: '24px',
                borderColor: hexError ? '#ef4444' : undefined,
                outline: hexError ? '1px solid #ef4444' : undefined,
              }}
            />
          </div>
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            {[
              { ch: 'r', label: 'R' },
              { ch: 'g', label: 'G' },
              { ch: 'b', label: 'B' },
            ].map(({ ch, label }) => (
              <div key={ch} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px' }}>
                <span style={{ fontSize: '10px', color: '#9ca3af', fontWeight: '600' }}>{label}</span>
                <input
                  type="number"
                  min="0"
                  max="255"
                  value={rgb[ch]}
                  onChange={(e) => handleRgbChange(ch, e.target.value)}
                  style={{
                    ...inputStyle,
                    fontSize: '13px',
                    width: '54px',
                    height: '36px',
                    padding: '4px 4px',
                    textAlign: 'center',
                  }}
                />
              </div>
            ))}
          </div>
        )}
      </div>
      {hexError && mode === 'hex' && (
        <div style={{ fontSize: '10px', color: '#ef4444', marginTop: '4px', paddingLeft: '48px' }}>
          HEX 형식이 올바르지 않습니다 (예: ff0000)
        </div>
      )}
    </div>
  );
};

const ColorPickerModal = ({
  value,
  onChange,
  onClose,
  showTransparent = false,
  showGradient = true,
  anchorPosition,
}) => {
  const isGradient = isGradientConfig(value);
  const [activeTab, setActiveTab] = useState(isGradient ? 'gradient' : 'solid');
  const [gradientConfig, setGradientConfig] = useState(
    isGradient ? value : createDefaultGradient(typeof value === 'string' && value !== 'transparent' ? value : '#3b82f6')
  );
  const [selectedStopIndex, setSelectedStopIndex] = useState(0);
  const modalRef = useRef(null);

  // 외부 클릭 감지
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (modalRef.current && !modalRef.current.contains(e.target)) {
        onClose();
      }
    };
    const timer = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
    }, 0);
    return () => {
      clearTimeout(timer);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [onClose]);

  // value 변경 시 gradient config 동기화
  useEffect(() => {
    if (isGradientConfig(value)) {
      setGradientConfig(value);
      setActiveTab('gradient');
    }
  }, [value]);

  // 팝업 위치 계산 (화면 밖으로 나가지 않도록)
  const getPosition = () => {
    if (!anchorPosition) return { top: '50%', left: '50%', transform: 'translate(-50%, -50%)' };
    const modalWidth = 380;
    const modalHeight = 480;
    let { top, left } = anchorPosition;

    if (left + modalWidth > window.innerWidth - 16) {
      left = window.innerWidth - modalWidth - 16;
    }
    if (top + modalHeight > window.innerHeight - 16) {
      top = window.innerHeight - modalHeight - 16;
    }
    if (left < 16) left = 16;
    if (top < 16) top = 16;

    return { top: `${top}px`, left: `${left}px` };
  };

  // --- 단색 핸들러 ---
  const handleSolidChange = (color) => {
    onChange(color);
  };

  // --- 그라데이션 핸들러 ---
  const updateGradient = useCallback((newConfig) => {
    setGradientConfig(newConfig);
    onChange(newConfig);
  }, [onChange]);

  const handleGradientTypeChange = (type) => {
    updateGradient({ ...gradientConfig, type });
  };

  // 숫자 텍스트 입력용: 숫자만 허용 + 클램핑
  const clampedNumInput = (value, min, max) => {
    const digits = value.replace(/[^0-9]/g, '');
    if (digits === '') return min;
    return Math.max(min, Math.min(max, parseInt(digits)));
  };

  const handleAngleChange = (angle) => {
    const clamped = clampedNumInput(String(angle), 0, 360);
    updateGradient({ ...gradientConfig, angle: clamped });
  };

  const handleStopColorChange = (color) => {
    const newStops = [...gradientConfig.colorStops];
    newStops[selectedStopIndex] = { ...newStops[selectedStopIndex], color };
    const newConfig = { ...gradientConfig, colorStops: newStops };
    setGradientConfig(newConfig);
    onChange(newConfig);
  };

  const handleStopOpacityChange = (rawVal) => {
    const val = clampedNumInput(String(rawVal), 0, 100) / 100;
    const newStops = [...gradientConfig.colorStops];
    newStops[selectedStopIndex] = { ...newStops[selectedStopIndex], opacity: val };
    updateGradient({ ...gradientConfig, colorStops: newStops });
  };

  const handleStopOffsetChange = (rawVal) => {
    const val = clampedNumInput(String(rawVal), 0, 100);
    const newStops = [...gradientConfig.colorStops];
    newStops[selectedStopIndex] = { ...newStops[selectedStopIndex], offset: val / 100 };
    updateGradient({ ...gradientConfig, colorStops: newStops });
  };

  const handleStopMove = (index, newOffset) => {
    const newStops = [...gradientConfig.colorStops];
    newStops[index] = { ...newStops[index], offset: newOffset };
    updateGradient({ ...gradientConfig, colorStops: newStops });
  };

  const handleStopAdd = (offset) => {
    const sorted = [...gradientConfig.colorStops].sort((a, b) => a.offset - b.offset);
    let beforeStop = sorted[0];
    for (let i = 0; i < sorted.length - 1; i++) {
      if (sorted[i].offset <= offset && sorted[i + 1].offset >= offset) {
        beforeStop = sorted[i];
        break;
      }
    }
    const newStop = {
      offset,
      color: beforeStop.color,
      opacity: 1,
    };
    const newStops = [...gradientConfig.colorStops, newStop];
    const newIndex = newStops.length - 1;
    setSelectedStopIndex(newIndex);
    updateGradient({ ...gradientConfig, colorStops: newStops });
  };

  const handleStopDelete = () => {
    if (gradientConfig.colorStops.length <= 2) return;
    const newStops = gradientConfig.colorStops.filter((_, i) => i !== selectedStopIndex);
    const newIndex = Math.min(selectedStopIndex, newStops.length - 1);
    setSelectedStopIndex(newIndex);
    updateGradient({ ...gradientConfig, colorStops: newStops });
  };

  const handlePresetGradient = (preset) => {
    const config = { ...preset };
    setGradientConfig(config);
    setSelectedStopIndex(0);
    onChange(config);
  };

  const currentSolidColor = isGradient ? '#3b82f6' : (value || '#000000');
  const selectedStop = gradientConfig.colorStops[selectedStopIndex];

  const pos = getPosition();

  return (
    <div style={{
      position: 'fixed',
      top: 0, left: 0, right: 0, bottom: 0,
      zIndex: 10000,
    }}>
      <div
        ref={modalRef}
        style={{
          position: 'absolute',
          ...pos,
          width: '380px',
          maxHeight: '520px',
          overflowY: 'auto',
          backgroundColor: '#ffffff',
          borderRadius: '12px',
          boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
          border: '1px solid #e5e7eb',
          zIndex: 10001,
        }}
      >
        {/* Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '12px 16px',
          borderBottom: '1px solid #f3f4f6',
        }}>
          <span style={{ fontSize: '14px', fontWeight: '600', color: '#1f2937' }}>색상</span>
          <button
            onClick={onClose}
            style={{
              background: 'none', border: 'none', cursor: 'pointer',
              padding: '4px', borderRadius: '4px', display: 'flex',
              color: '#6b7280',
            }}
          >
            <FiX size={16} />
          </button>
        </div>

        {/* Tabs */}
        {showGradient && (
          <div style={{ display: 'flex', padding: '8px 16px 0', gap: '4px' }}>
            <button
              onClick={() => setActiveTab('solid')}
              style={{
                flex: 1,
                padding: '8px',
                border: 'none',
                borderRadius: '6px',
                backgroundColor: activeTab === 'solid' ? '#3b82f6' : '#f3f4f6',
                color: activeTab === 'solid' ? '#ffffff' : '#6b7280',
                fontSize: '13px',
                fontWeight: '500',
                cursor: 'pointer',
              }}
            >
              단색
            </button>
            <button
              onClick={() => {
                setActiveTab('gradient');
                if (!isGradient) {
                  const baseColor = typeof value === 'string' && value !== 'transparent' ? value : '#3b82f6';
                  const newConfig = createDefaultGradient(baseColor);
                  setGradientConfig(newConfig);
                  onChange(newConfig);
                }
              }}
              style={{
                flex: 1,
                padding: '8px',
                border: 'none',
                borderRadius: '6px',
                backgroundColor: activeTab === 'gradient' ? '#3b82f6' : '#f3f4f6',
                color: activeTab === 'gradient' ? '#ffffff' : '#6b7280',
                fontSize: '13px',
                fontWeight: '500',
                cursor: 'pointer',
              }}
            >
              그라데이션
            </button>
          </div>
        )}

        <div style={{ padding: '12px 16px 16px' }}>
          {/* === 단색 탭 === */}
          {activeTab === 'solid' && (
            <>
              {/* 기본 팔레트 */}
              <label style={{ display: 'block', fontSize: '12px', color: '#6b7280', marginBottom: '8px' }}>
                기본 팔레트
              </label>
              <div style={{ display: 'flex', gap: '5px', flexWrap: 'wrap', marginBottom: '12px' }}>
                {showTransparent && (
                  <button
                    onClick={() => handleSolidChange('transparent')}
                    style={{
                      width: '26px', height: '26px', borderRadius: '4px',
                      border: value === 'transparent' ? '2px solid #3b82f6' : '1px solid #e5e7eb',
                      backgroundColor: '#ffffff', cursor: 'pointer', padding: 0,
                      position: 'relative', overflow: 'hidden',
                    }}
                  >
                    <div style={{
                      position: 'absolute', top: '50%', left: '-5px',
                      width: '40px', height: '2px', backgroundColor: '#ef4444',
                      transform: 'rotate(-45deg)',
                    }} />
                  </button>
                )}
                {PRESET_COLORS.map((color) => (
                  <button
                    key={color}
                    onClick={() => handleSolidChange(color)}
                    style={{
                      width: '26px', height: '26px', borderRadius: '4px',
                      border: value === color ? '2px solid #3b82f6' : '1px solid #e5e7eb',
                      backgroundColor: color, cursor: 'pointer', padding: 0,
                    }}
                  />
                ))}
              </div>

              {/* Color input (HEX/RGB) */}
              <ColorInput
                color={currentSolidColor === 'transparent' ? '#000000' : currentSolidColor}
                onChange={handleSolidChange}
              />
            </>
          )}

          {/* === 그라데이션 탭 === */}
          {activeTab === 'gradient' && (
            <>
              {/* Type toggle */}
              <div style={{ display: 'flex', gap: '4px', marginBottom: '12px' }}>
                <button
                  onClick={() => handleGradientTypeChange('linear')}
                  style={{
                    flex: 1, padding: '6px', border: 'none', borderRadius: '6px',
                    backgroundColor: gradientConfig.type === 'linear' ? '#e0e7ff' : '#f3f4f6',
                    color: gradientConfig.type === 'linear' ? '#3b82f6' : '#6b7280',
                    fontSize: '12px', fontWeight: '500', cursor: 'pointer',
                  }}
                >
                  Linear
                </button>
                <button
                  onClick={() => handleGradientTypeChange('radial')}
                  style={{
                    flex: 1, padding: '6px', border: 'none', borderRadius: '6px',
                    backgroundColor: gradientConfig.type === 'radial' ? '#e0e7ff' : '#f3f4f6',
                    color: gradientConfig.type === 'radial' ? '#3b82f6' : '#6b7280',
                    fontSize: '12px', fontWeight: '500', cursor: 'pointer',
                  }}
                >
                  Radial
                </button>
              </div>

              {/* Gradient bar */}
              <GradientBar
                colorStops={gradientConfig.colorStops}
                gradientType={gradientConfig.type}
                selectedStopIndex={selectedStopIndex}
                onStopSelect={setSelectedStopIndex}
                onStopMove={handleStopMove}
                onStopAdd={handleStopAdd}
              />

              {/* Selected stop editor */}
              {selectedStop && (
                <div style={{
                  padding: '10px',
                  backgroundColor: '#f9fafb',
                  borderRadius: '8px',
                  marginBottom: '12px',
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '8px' }}>
                    <ColorInput
                      color={selectedStop.color}
                      onChange={handleStopColorChange}
                      style={{ flex: 1 }}
                    />
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '8px' }}>
                    <span style={{ fontSize: '12px', color: '#6b7280', width: '36px', flexShrink: 0 }}>위치</span>
                    <input
                      type="range"
                      min="0"
                      max="100"
                      value={Math.round(selectedStop.offset * 100)}
                      onChange={(e) => handleStopOffsetChange(e.target.value)}
                      style={{ flex: 1 }}
                    />
                    <input
                      type="text"
                      inputMode="numeric"
                      value={Math.round(selectedStop.offset * 100)}
                      onChange={(e) => handleStopOffsetChange(e.target.value)}
                      style={{ ...inputStyle, fontSize: '12px', width: '54px', flex: 'none', textAlign: 'center' }}
                    />
                    <span style={{ fontSize: '11px', color: '#9ca3af', width: '14px' }}>%</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <span style={{ fontSize: '12px', color: '#6b7280', width: '36px', flexShrink: 0 }}>투명</span>
                    <input
                      type="range"
                      min="0"
                      max="100"
                      value={Math.round(selectedStop.opacity * 100)}
                      onChange={(e) => handleStopOpacityChange(e.target.value)}
                      style={{ flex: 1 }}
                    />
                    <input
                      type="text"
                      inputMode="numeric"
                      value={Math.round(selectedStop.opacity * 100)}
                      onChange={(e) => handleStopOpacityChange(e.target.value)}
                      style={{ ...inputStyle, fontSize: '12px', width: '54px', flex: 'none', textAlign: 'center' }}
                    />
                    <span style={{ fontSize: '11px', color: '#9ca3af', width: '14px' }}>%</span>
                  </div>
                  {gradientConfig.colorStops.length > 2 && (
                    <div style={{ textAlign: 'right', marginTop: '6px' }}>
                      <button
                        onClick={handleStopDelete}
                        style={{
                          padding: '4px 10px', border: 'none',
                          borderRadius: '4px', backgroundColor: '#fee2e2', color: '#ef4444',
                          fontSize: '11px', cursor: 'pointer',
                        }}
                      >
                        스톱 삭제
                      </button>
                    </div>
                  )}
                </div>
              )}

              {/* Angle slider (linear only) */}
              {gradientConfig.type === 'linear' && (
                <div style={{ marginBottom: '12px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <span style={{ fontSize: '12px', color: '#6b7280', width: '36px', flexShrink: 0 }}>각도</span>
                    <input
                      type="range"
                      min="0"
                      max="360"
                      value={gradientConfig.angle ?? 0}
                      onChange={(e) => handleAngleChange(e.target.value)}
                      style={{ flex: 1 }}
                    />
                    <input
                      type="text"
                      inputMode="numeric"
                      value={gradientConfig.angle ?? 0}
                      onChange={(e) => handleAngleChange(e.target.value)}
                      style={{ ...inputStyle, fontSize: '12px', width: '54px', flex: 'none', textAlign: 'center' }}
                    />
                    <span style={{ fontSize: '11px', color: '#9ca3af', width: '14px' }}>°</span>
                  </div>
                </div>
              )}

              {/* Gradient presets */}
              <label style={{ display: 'block', fontSize: '12px', color: '#6b7280', marginBottom: '8px' }}>
                프리셋
              </label>
              <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                {PRESET_GRADIENTS.map((preset, i) => (
                  <button
                    key={i}
                    onClick={() => handlePresetGradient(preset)}
                    style={{
                      width: '36px', height: '36px', borderRadius: '6px',
                      border: '1px solid #e5e7eb', cursor: 'pointer', padding: 0,
                      background: toCssGradient(preset),
                    }}
                  />
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ColorPickerModal;
